import { PPA_SCOPE_DIMENSIONS, parseScopeDimToSelection } from 'hooks/ppa/ppaScope.helpers';
import type { PricePackArchitectureRequest } from 'hooks/ppa/useFetchPricePackArchitecture';
import { formatWord } from 'utils/helperMethods';

type ScopeInclusion = {
    attributeType?: string;
};

type TimePeriodOption = {
    timePeriod?: string;
    startTimestamp?: number;
    endTimestamp?: number;
};

const formatTimePeriodLabel = (label: string) => {
    const trimmed = label.trim();
    if (!trimmed) return '';
    return trimmed.length < 5 ? trimmed.toUpperCase() : trimmed;
};

const parseScopeInclusions = (scopeValue: unknown): ScopeInclusion[] => {
    try {
        if (!scopeValue) return [];
        const parsed = typeof scopeValue === 'string' ? JSON.parse(scopeValue) : scopeValue;
        return parsed?.inclusion ?? [];
    } catch {
        return [];
    }
};

const getScopeAttributeTypes = (persistedScope?: Record<string, unknown> | null) => {
    const types = new Set<string>();
    if (!persistedScope) return types;

    PPA_SCOPE_DIMENSIONS.forEach((dimension) => {
        parseScopeInclusions(persistedScope[dimension]).forEach((inclusion) => {
            const attributeType = inclusion.attributeType?.toLowerCase();
            if (attributeType) types.add(attributeType);
        });
    });

    return types;
};

const buildScopeSummary = (persistedScope?: Record<string, unknown> | null) => {
    if (!persistedScope) return undefined;

    const scopeParts = PPA_SCOPE_DIMENSIONS.map((dimension) =>
        parseScopeDimToSelection(persistedScope[dimension]),
    ).filter(Boolean) as string[];

    return scopeParts.length > 0 ? scopeParts.join(' - ') : undefined;
};

const resolveTimePeriodLabel = ({
    timeSelectionLabel,
    chartRequest,
    timePeriodList,
}: {
    timeSelectionLabel?: string;
    chartRequest?: PricePackArchitectureRequest | null;
    timePeriodList?: TimePeriodOption[];
}) => {
    if (timeSelectionLabel?.trim()) {
        return formatTimePeriodLabel(timeSelectionLabel);
    }

    const timePeriod = chartRequest?.time_period;
    if (!timePeriod?.start_timestamp || !timePeriod?.end_timestamp || !timePeriodList?.length) {
        return undefined;
    }

    const match = timePeriodList.find(
        (period) =>
            period.startTimestamp === timePeriod.start_timestamp &&
            period.endTimestamp === timePeriod.end_timestamp,
    );

    return match?.timePeriod ? formatTimePeriodLabel(match.timePeriod) : undefined;
};

export const buildPackArchitectureGlobalFiltersSummary = ({
    timeSelectionLabel,
    chartRequest,
    timePeriodList,
    ppaPersistedScope,
    allCheckedAttribute,
}: {
    timeSelectionLabel?: string;
    chartRequest?: PricePackArchitectureRequest | null;
    timePeriodList?: TimePeriodOption[];
    ppaPersistedScope?: Record<string, unknown> | null;
    allCheckedAttribute?: Record<string, Record<string, unknown>>;
}) => {
    const parts: string[] = [];

    const timePeriodLabel = resolveTimePeriodLabel({
        timeSelectionLabel,
        chartRequest,
        timePeriodList,
    });
    if (timePeriodLabel) {
        parts.push(`Time Period: ${timePeriodLabel}`);
    }

    const scopeSummary = buildScopeSummary(ppaPersistedScope);
    if (scopeSummary) {
        parts.push(`Scope: ${scopeSummary}`);
    }

    const scopeAttributeTypes = getScopeAttributeTypes(ppaPersistedScope);
    Object.entries(allCheckedAttribute || {})
        .map(([attributeType, selectedValues]) => ({
            attributeType,
            label: formatWord(attributeType),
            items: Object.keys((selectedValues as Record<string, unknown>) || {}),
        }))
        .filter(
            (group) =>
                group.items.length > 0 &&
                !scopeAttributeTypes.has(group.attributeType.toLowerCase()),
        )
        .sort((left, right) => left.label.localeCompare(right.label))
        .forEach((group) => {
            parts.push(`${group.label}: ${group.items.join(', ')}`);
        });

    return parts.join(' | ');
};
